/**
 * ITER 69 — Widget "Lead per Convenzione" (matrice partner × ramo canonico).
 *
 * C-level KPI: mostra la ripartizione dei lead per convenzione negli ultimi
 * 30 giorni (toggle 7g/30g/90g/YTD/Custom). Ogni cella è cliccabile e naviga
 * a `/admin/richieste?partner=X&ramo=Y&period=Z` per il drill-down.
 *
 * Backend endpoint: `GET /api/admin/kpi/lead-per-convenzione?period=30d`
 *
 * Accesso: staff (super_admin, admin, dipendente) via RBAC lato endpoint.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Users, RefreshCw, Loader2, ArrowRight, Filter, TrendingUp,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIODS = [
  { key: "7d",  label: "7g" },
  { key: "30d", label: "30g" },
  { key: "90d", label: "90g" },
  { key: "ytd", label: "YTD" },
  { key: "custom", label: "Custom" },
];

// Etichette umane per convenzioni note (fallback: uppercase primo char).
const PARTNER_LABELS = {
  stellantis:   "Stellantis",
  leonardo:     "Leonardo",
  gori:         "Gori",
  unicredit:    "Unicredit",
  ge_avio:      "GE Avio",
  hitachi:      "Hitachi",
  __diretto__:  "Diretto (senza convenzione)",
};

function labelFor(p) {
  return PARTNER_LABELS[p] || (p.charAt(0).toUpperCase() + p.slice(1));
}

/** Colore cella in base al valore (heatmap). */
function heatColor(value, max) {
  if (!max || value === 0) return "#F8FAFC"; // slate-50 pallido
  const ratio = Math.min(1, value / max);
  // scala da slate → verde forestale (Colucci brand)
  if (ratio < 0.15) return "#F0F5F1";
  if (ratio < 0.35) return "#DDEBE0";
  if (ratio < 0.55) return "#B9D6C0";
  if (ratio < 0.75) return "#89BB95";
  return "#4E9863";
}
function heatTextColor(value, max) {
  if (!max || value === 0) return "#94A3B8";
  const ratio = Math.min(1, value / max);
  return ratio > 0.55 ? "#FFFFFF" : "#0F1F14";
}

export default function LeadPerConvenzioneWidget() {
  const [period, setPeriod] = useState("30d");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = { period };
      if (period === "custom") {
        if (!customFrom || !customTo) {
          setError("Seleziona entrambe le date custom.");
          setLoading(false);
          return;
        }
        params.custom_from = customFrom;
        params.custom_to = customTo;
      }
      const { data: resp } = await axios.get(
        `${API}/admin/kpi/lead-per-convenzione`,
        { params, withCredentials: true },
      );
      setData(resp);
    } catch (e) {
      const msg = e.response?.data?.detail || "Errore caricamento matrice";
      setError(msg);
      toast.error(msg);
    } finally { setLoading(false); }
  }, [period, customFrom, customTo]);

  useEffect(() => { load(); }, [load]);

  const maxCell = useMemo(() => {
    if (!data?.matrix) return 0;
    let m = 0;
    for (const row of Object.values(data.matrix)) {
      for (const [k, v] of Object.entries(row)) {
        if (k !== "_total" && v > m) m = v;
      }
    }
    return m;
  }, [data]);

  return (
    <div className="bg-white border border-[#E5E2DB] rounded-2xl shadow-sm p-5"
         data-testid="lead-per-convenzione-widget">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-emerald-100">
              <Users className="w-4 h-4 text-emerald-700" />
            </div>
            <h3 className="font-heading text-base font-semibold text-[#0F1F14]"
                data-testid="lpc-title">
              Lead per Convenzione
            </h3>
          </div>
          <p className="text-[11px] text-slate-500 mt-1">
            Matrice partner × ramo canonico · click cella per drill-down
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg
                     border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
          data-testid="lpc-refresh-btn"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <RefreshCw className="w-3.5 h-3.5" />}
          Aggiorna
        </button>
      </div>

      {/* Period toggle */}
      <div className="flex items-center flex-wrap gap-2 mb-3"
           data-testid="lpc-period-toggle">
        {PERIODS.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => setPeriod(p.key)}
            className={`text-xs px-2.5 py-1 rounded-full border transition-all
              ${period === p.key
                ? "bg-[#163321] text-white border-[#163321]"
                : "bg-white text-slate-700 border-slate-200 hover:border-[#163321]/40"}`}
            data-testid={`lpc-period-${p.key}`}
          >
            {p.label}
          </button>
        ))}
      </div>
      {period === "custom" && (
        <div className="flex items-center gap-2 mb-3 text-[12px]">
          <input type="date" value={customFrom}
                 onChange={(e) => setCustomFrom(e.target.value)}
                 className="border rounded-lg px-2 py-1"
                 data-testid="lpc-custom-from" />
          <span className="text-slate-400">→</span>
          <input type="date" value={customTo}
                 onChange={(e) => setCustomTo(e.target.value)}
                 className="border rounded-lg px-2 py-1"
                 data-testid="lpc-custom-to" />
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg
                        p-2.5 mb-3 text-xs" data-testid="lpc-error">
          {error}
        </div>
      )}

      {loading && !data && (
        <div className="text-sm text-slate-500 flex items-center gap-2 py-6 justify-center"
             data-testid="lpc-loading">
          <Loader2 className="w-4 h-4 animate-spin" />
          Caricamento matrice…
        </div>
      )}

      {data && (
        <>
          {/* Summary bar */}
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-3 text-xs"
               data-testid="lpc-summary">
            <span className="font-heading text-xl text-[#0F1F14]">
              {data.totale_generale}
            </span>
            <span className="text-slate-500">lead totali</span>
            <span className="text-slate-400">·</span>
            <span className="text-slate-500">
              {data.partners.length} {data.partners.length === 1 ? "partner" : "partner"}
              {" attivi"}
            </span>
            <span className="text-slate-400">·</span>
            <span className="text-slate-500">
              {data.range.from.slice(0, 10)} → {data.range.to.slice(0, 10)}
            </span>
            {data.cache_hit && (
              <span className="text-[10px] text-emerald-700 bg-emerald-50 px-1.5 rounded">
                cache
              </span>
            )}
          </div>

          {/* Matrice */}
          {data.partners.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center border border-dashed
                            border-slate-200 rounded-lg" data-testid="lpc-empty">
              Nessun lead nel periodo selezionato.
            </div>
          ) : (
            <div className="overflow-x-auto -mx-1 px-1"
                 data-testid="lpc-matrix-wrapper">
              <table className="w-full text-[12px] border-collapse"
                     data-testid="lpc-matrix">
                <thead>
                  <tr>
                    <th className="text-left font-semibold text-slate-600 py-1.5 px-2
                                   sticky left-0 bg-white z-10 border-b border-slate-200">
                      Partner
                    </th>
                    {data.rami.map((r) => (
                      <th key={r.key}
                          className="text-center font-semibold text-slate-600 py-1.5
                                     px-1 border-b border-slate-200 min-w-[60px]"
                          data-testid={`lpc-th-${r.key}`}>
                        {r.label}
                      </th>
                    ))}
                    <th className="text-center font-semibold text-slate-800 py-1.5 px-2
                                   border-b border-slate-200 bg-slate-50">
                      Totale
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.partners.map((p) => {
                    const row = data.matrix[p];
                    const isDiretto = p === "__diretto__";
                    return (
                      <tr key={p}
                          className={`hover:bg-slate-50/50 ${isDiretto ? "border-t-2 border-slate-300" : ""}`}
                          data-testid={`lpc-row-${p}`}>
                        <td className="py-1.5 px-2 font-medium text-slate-800
                                       sticky left-0 bg-white border-b border-slate-100">
                          {labelFor(p)}
                        </td>
                        {data.rami.map((r) => {
                          const v = row[r.key] || 0;
                          const cellColor = heatColor(v, maxCell);
                          const txtColor = heatTextColor(v, maxCell);
                          const drillHref = v > 0
                            ? `/admin/richieste?partner=${encodeURIComponent(p)}&ramo=${encodeURIComponent(r.key)}`
                            : null;
                          return (
                            <td key={r.key}
                                className="text-center py-0.5 px-0.5 border-b border-slate-100">
                              {drillHref ? (
                                <Link to={drillHref}
                                      className="block rounded font-semibold py-1 hover:ring-2 hover:ring-[#D97B58] transition-all"
                                      style={{ background: cellColor, color: txtColor }}
                                      data-testid={`lpc-cell-${p}-${r.key}`}
                                      title={`${labelFor(p)} × ${r.label}: ${v} lead — click per dettaglio`}>
                                  {v}
                                </Link>
                              ) : (
                                <span className="block rounded py-1 text-slate-300"
                                      style={{ background: cellColor }}
                                      data-testid={`lpc-cell-${p}-${r.key}`}>
                                  {v}
                                </span>
                              )}
                            </td>
                          );
                        })}
                        <td className="text-center py-1.5 px-2 font-heading text-sm
                                       text-[#0F1F14] bg-slate-50 border-b border-slate-100"
                            data-testid={`lpc-total-${p}`}>
                          {row._total}
                        </td>
                      </tr>
                    );
                  })}
                  {/* Riga totali colonna */}
                  <tr className="bg-slate-100 font-semibold" data-testid="lpc-row-totali">
                    <td className="py-1.5 px-2 text-slate-700 sticky left-0 bg-slate-100">
                      Totale
                    </td>
                    {data.rami.map((r) => (
                      <td key={r.key}
                          className="text-center py-1.5 px-1 text-slate-800"
                          data-testid={`lpc-col-total-${r.key}`}>
                        {data.totali_per_ramo[r.key] || 0}
                      </td>
                    ))}
                    <td className="text-center py-1.5 px-2 font-heading text-base
                                   text-[#163321] bg-slate-200"
                        data-testid="lpc-grand-total">
                      {data.totale_generale}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Legenda heatmap */}
          <div className="mt-3 flex items-center gap-2 text-[10px] text-slate-500"
               data-testid="lpc-legend">
            <span>Intensità:</span>
            {[0, 0.15, 0.35, 0.55, 0.75, 1].map((r, i) => (
              <span key={i} className="w-4 h-3 rounded-sm border border-slate-200 inline-block"
                    style={{ background: heatColor(Math.round(maxCell * r), maxCell) }} />
            ))}
            <span className="ml-1">0 → {maxCell}</span>
          </div>
        </>
      )}
    </div>
  );
}
